import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, FileText, ChevronRight, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { iconFor } from "@/lib/iconMap";
import { safeFormatDate, safeFormatDateOnly } from "@/lib/dates";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { WeatherChip } from "@/components/WeatherChip";
import { QUICK_EDIT_EVENT } from "@/hooks/useQuickEditRefresh";

const REFRESH_INTERVAL_MS = 30 * 1000;  // auto-refresh every 30 seconds

/**
 * Dashboard daily-summary card.
 *
 * - Renders rich, named highlight lines (horse names, currency totals, feed
 *   trend) returned by /api/dashboard/daily-summary.
 * - The header is a Link to /changes for the full report.
 * - Bill follow-ups (overdue + upcoming) are actionable inline (mark paid / snooze).
 * - Waits for AuthContext to finish loading before firing its first fetch,
 *   with one automatic retry on 5xx / network errors — this eliminates the
 *   "empty card on first login" flash new users used to see while the
 *   backend was still provisioning their user record.
 */
export function DailySummaryCard() {
  const { loading: authLoading, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const isMounted = useRef(true);

  const load = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    // One-shot retry helper for transient auth / provisioning races.
    // 5xx and network errors are retryable; a 401/403 is not — the token
    // is either invalid or the user genuinely lacks permission and no
    // retry will change that.
    const attempt = async () => {
      const res = await api.get("/dashboard/daily-summary");
      return res.data;
    };
    try {
      let body;
      try {
        body = await attempt();
      } catch (e) {
        const status = e?.response?.status;
        const retryable = !status || status >= 500;
        if (!retryable) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        body = await attempt();
      }
      if (!isMounted.current) return;
      setData(body);
      setLastRefreshAt(new Date());
    } catch (e) {
      // Silent on background refresh failures — keep last good data
      if (showSpinner) console.error(e);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    // Wait for the user record to be provisioned before hitting the
    // dashboard endpoints. Firing pre-auth used to race with the
    // singleflight lock on the backend and occasionally return an
    // empty payload before the user had `dashboard:read`.
    if (authLoading || !user) return () => { isMounted.current = false; };
    load(true);

    // Auto-refresh while the tab is visible — pause when hidden to save quota
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => load(false), REFRESH_INTERVAL_MS);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load(false);  // immediate refresh on return
        start();
      } else stop();
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);

    // Also re-fetch immediately whenever a Quick Edit applies anywhere in
    // the app — the daily-summary numbers must reflect those changes too.
    const onQuickEdit = () => load(false);
    window.addEventListener(QUICK_EDIT_EVENT, onQuickEdit);

    return () => {
      isMounted.current = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener(QUICK_EDIT_EVENT, onQuickEdit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  const markPaid = async (id) => {
    setActingId(id);
    try {
      await api.post(`/invoices/${id}/mark-paid`);
      toast.success("Marked as paid");
      load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as paid");
    } finally { setActingId(null); }
  };
  const snooze = async (id) => {
    setActingId(id);
    try {
      await api.post(`/invoices/${id}/snooze`, { days: 7 });
      toast.success("Snoozed 7 days");
      load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not snooze");
    } finally { setActingId(null); }
  };

  if (loading) {
    return (
      <Card className="rounded-xl" data-testid="daily-summary-loading">
        <CardHeader><CardTitle className="text-base font-medium">Today at a glance</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const overdue = data.bills?.overdue || [];
  const upcoming = data.bills?.upcoming || [];
  const highlights = data.highlights || [];

  return (
    <Card className="rounded-xl" data-testid="daily-summary-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-medium">Today at a glance</CardTitle>
            <LiveBadge lastAt={lastRefreshAt} />
          </div>
          <p className="text-xs text-muted-foreground" data-testid="daily-summary-date">
            {safeFormatDateOnly(data.today_sast, "EEEE, d MMMM yyyy", "today")} · SAST
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/changes"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground hover:text-[hsl(var(--ap-forest))] transition-colors"
            data-testid="daily-summary-see-full"
          >
            See full changes <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Weather strip. The Crags / Plett current conditions */}
        <WeatherChip variant="inline" />

        {/* Rich highlight lines */}
        <div className="space-y-1.5" data-testid="daily-summary-highlights">
          {highlights.map((h, i) => {
            const HIcon = iconFor(h.icon);
            return (
              <div key={`${h.kind}-${h.label || h.title || i}-${i}`} className="text-sm leading-relaxed flex items-start gap-2.5" data-testid={`daily-summary-highlight-${h.kind}-${i}`}>
                <HIcon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                <span className="flex-1">{h.text}</span>
              </div>
            );
          })}
        </div>

        {/* Bill follow-ups */}
        {(overdue.length > 0 || upcoming.length > 0) ? (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <FileText className="h-3.5 w-3.5" />
              Bills to follow up
              <span className="ml-auto normal-case tracking-normal text-[11px]">
                {overdue.length} overdue · {upcoming.length} upcoming
              </span>
            </div>
            {overdue.slice(0, 3).map((b) => (
              <BillRow key={b.id} bill={b} variant="overdue"
                acting={actingId === b.id}
                onPay={() => markPaid(b.id)}
                onSnooze={() => snooze(b.id)} />
            ))}
            {upcoming.slice(0, 3).map((b) => (
              <BillRow key={b.id} bill={b} variant="upcoming"
                acting={actingId === b.id}
                onPay={() => markPaid(b.id)}
                onSnooze={() => snooze(b.id)} />
            ))}
            {(overdue.length + upcoming.length) > 6 && (
              <Link to="/changes" className="text-xs text-muted-foreground hover:text-foreground pt-1 inline-block">
                + {(overdue.length + upcoming.length) - 6} more →
              </Link>
            )}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground pt-2 border-t flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            No outstanding bills due in the next 30 days.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BillRow({ bill, variant, acting, onPay, onSnooze }) {
  const isOverdue = variant === "overdue";
  const tint = isOverdue
    ? "border-red-300/50 bg-red-50/40"
    : bill.days_until_due <= 7
      ? "border-amber-300/50 bg-amber-50/40"
      : "border-border bg-background";
  const Icon = isOverdue ? AlertTriangle : Clock;
  const iconColor = isOverdue ? "text-red-600" : (bill.days_until_due <= 7 ? "text-amber-600" : "text-muted-foreground");

  let dueLabel;
  if (bill.days_until_due < 0) dueLabel = `${Math.abs(bill.days_until_due)}d overdue`;
  else if (bill.days_until_due === 0) dueLabel = "Due today";
  else if (bill.days_until_due === 1) dueLabel = "Due tomorrow";
  else dueLabel = `Due in ${bill.days_until_due}d`;

  return (
    <div className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${tint}`} data-testid={`daily-summary-bill-${bill.id}`}>
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${iconColor}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate" title={bill.supplier}>
            {bill.supplier}
            {bill.invoice_number && <span className="text-muted-foreground font-normal ml-2 text-xs">#{bill.invoice_number}</span>}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>{dueLabel}</span>
            <span className="text-muted-foreground/60">·</span>
            <span>{safeFormatDate(bill.due_date, "MMM d, yyyy")}</span>
            {bill.total != null && (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span className="tabular-nums font-medium text-foreground">
                  {formatCurrency(bill.total, bill.currency)}
                </span>
              </>
            )}
            {bill.category && (
              <span className="capitalize text-[10px] uppercase tracking-wider text-muted-foreground/70 bg-muted px-1.5 py-0.5 rounded">
                {bill.category}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="ghost" disabled={acting} onClick={onSnooze} className="h-8 px-2 text-xs" data-testid={`daily-summary-bill-snooze-${bill.id}`}>
          Snooze 7d
        </Button>
        <Button size="sm" disabled={acting} onClick={onPay} className="h-8 px-3 text-xs bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90" data-testid={`daily-summary-bill-pay-${bill.id}`}>
          {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : <>Mark paid</>}
        </Button>
      </div>
    </div>
  );
}


/**
 * Small pulsing "Live" indicator that turns amber if it's been more than
 * 2 minutes since the last successful refresh.
 */
function LiveBadge({ lastAt }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const ageMs = lastAt ? Date.now() - lastAt.getTime() : null;
  const stale = ageMs != null && ageMs > 2 * 60 * 1000;
  const label = !lastAt
    ? "Connecting…"
    : stale
      ? "Reconnecting…"
      : "Live";
  const dotColor = stale ? "bg-amber-500" : "bg-emerald-500";
  const ringColor = stale ? "bg-amber-500/40" : "bg-emerald-500/40";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground"
      title={lastAt ? `Last refreshed ${lastAt.toLocaleTimeString()}` : "Establishing connection"}
      data-testid="daily-summary-live-badge"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className={`absolute inline-flex h-full w-full rounded-full ${ringColor} ${stale ? "" : "animate-ping"}`} />
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${dotColor}`} />
      </span>
      {label}
    </span>
  );
}
